import { IonCheckbox, IonItem } from "@ionic/react";
import { CollapsableTable, CollapseLevel } from "components/CollapsableTable";
import { DropdownSelectItem }              from "components/DropdownSelect";
import { OptionsButton }                   from "components/OptionsButton";
import { Page }                            from "components/Page";
import { HarmonicsChart }                  from "components/charts/HarmonicsChart";
import { DvcPage }   from "navigation/DvcPage";
import { RouterMap } from "navigation/RouterMap";
import { useDeviceConnection } from "functions/Context";
import { FormatNumber }        from "functions/Formatting";
import { subscripted }         from "functions/GeneralTranslations";
import { Glob, useLoc }        from "functions/Language";
import { HarmResponse, HarmSimple, TYPE_GCR, TYPE_GCRT, TYPE_ICR, TYPE_PLA } from "functions/networking/NetTypes";
import { useLoader }                                               from "functions/networking/useLoader";
import { FC, ReactNode, useMemo, useState } from "react";

import harmIcon from "img/harmonics.svg";
import { optionsOutline } from "ionicons/icons";


const route = RouterMap.harmonics;

const requests : HarmSimple[] = [
  "Harm_U_LN",
  "Harm_U_LL",
  "Harm_Current",
  "Harm_U_LN_Percent",
  "Harm_U_LL_Percent",
  "Harm_Current_Percent"
];

function ReqName(mode : modes, percent : number) : HarmSimple {
  if (mode > 2 || percent > 1) throw "Invalid arguents";
  return requests[mode + 3 * percent];
}

enum modes {
  ULN = 0,
  ULL = 1,
  I = 2,
}

const modeNames = {
	[modes.ULN]: subscripted.uln,
	[modes.ULL]: subscripted.ull,
	[modes.I]  : "I",
};

const empty_harm = {
	Data : { nr_phases: 1, values: [], times: [] } as HarmResponse,
	Head: { _type: TYPE_PLA }
};

const colTitles = ["L1", "L2", "L3"];
const llTitles = ["L1 - L2", "L2 - L3", "L3 - L1"];

const Content : FC = () => {
	const dvc = useDeviceConnection().device._type;

  const loc = useLoc(glob);
  const [percent, setPercent] = useState(1);
  const [mode, setMode] = useState((dvc === TYPE_GCR || dvc == TYPE_GCRT) ? modes.ULL : modes.ULN);
  const [mm, setMm] = useState(false);
  const phaseNames = useMemo(
    () => mode === modes.ULL ? [loc.all, "L1-L2", "L2-L3", "L3-L1"] : [loc.all, "L1", "L2", "L3"]
  , [mode]);
  const loader    = useLoader(   ReqName(mode, percent)      , empty_harm);
  const maxLoader = useLoader(`${ReqName(mode, percent)}_Max`, empty_harm);
  const avgLoader = useLoader(`${ReqName(mode, percent)}_Avg`, empty_harm, { disabled: dvc === TYPE_GCR || dvc == TYPE_GCRT });

  const loaders = [loader, maxLoader, avgLoader];

	let quantities = [
		subscripted.uln,
		subscripted.ull,
		"I"
	];
	let quant_offset = 0;
	if (dvc === TYPE_GCR || dvc == TYPE_GCRT) {
		quantities.shift();
		quant_offset = 1;
	}

	let table_titles;
	if      (loader.data.Data.nr_phases === 1)
		table_titles = [loc.value];
	else if (mode === modes.ULL)
		table_titles = llTitles;
	else
		table_titles = colTitles;

	let filter : (_: any, i: number) => boolean;
	if (dvc === TYPE_GCR || dvc == TYPE_GCRT) filter = (_, i) => i%2===0;
	else                  filter = (_, __) => true;

	const unit = percent ? "%" : mode === modes.I ? "A" : "V";

  const Options = <OptionsButton icon={optionsOutline} title="">
    {_Close => <>
      <DropdownSelectItem
        label={loc.mode}
        selected={mode-quant_offset} setSelected={(val) => setMode(val+quant_offset)} options={quantities}
      />

			{dvc !== TYPE_GCR &&
				<DropdownSelectItem
					label={loc.show}
					selected={percent} setSelected={setPercent} options={loc.showOptions}
				/>
			}

      <IonItem>
        <IonCheckbox checked={mm} onIonChange={e => setMm(e.detail.checked)}>{loc.minMax}</IonCheckbox>
      </IonItem>
    </>}
  </OptionsButton>

  return <Page title={<>{loc.title} ({modeNames[mode]})</>} loaders={loaders} additionalButtons={Options}>
	{dvc && <>
    <HarmonicsChart
      device={loader.data.Head._type}
      data={loader.data.Data.values}
      phaseNames={phaseNames}
      units={percent ? "%" : mode === modes.I ? "A" : "V"}
      minMax={mm ? { min: maxLoader.data.Data.values, max: maxLoader.data.Data.values } : undefined}
      skipFirst={percent === 1}
			singlePhase={loader.data.Data.nr_phases === 1}
    />

		<CollapsableTable
			title={loc.table}
			collapse={CollapseLevel.closeAll}
			LL={mode === modes.ULL}
			data={loader.data.Data.values.map((now, i) => ({
				label: i+1,
				unit: unit,
				formatter: FormatNumber,
				values: now,
				avg: avgLoader.data.Data.values[i],
				max: maxLoader.data.Data.values[i],
				maxt: maxLoader.data.Data.times[i],
			})).filter(filter)}
			headerFields={table_titles}
			margin="50px"
		/>
	</>}</Page>
}


const glob = Glob({
	en: {
		title: "Harmonics",
		show: "Display",
		showOptions: ["Values", "Percents"] as [ReactNode, ReactNode],
		mode: "Quantity",
		all: "All",
		minMax: "Show maxima",

		options: "Options",

		table: "Values",
		harm:  "Order   ",
		value: "Value",
	},
	cs: {
		title: "Harmonické",
		show: "Zobrazení",
		showOptions: ["Hodnoty", "Procenta"],
		mode: "Veličina",
		all: "Všechny", // fáze
		minMax: "Zobrazovat maxima",

		options: "Možnosti",

		table: "Hodnoty",
		harm:  "Řád   ",
		value: "Hodnota",
	},
	pg: {
		title: "Harmônicos",
		show: "Mostrar",
		showOptions: ["Valores", "Procentagens"],
		mode: "Quantidade",
		all: "Todas",
		minMax: "Mostrar máximos",

		options: "Opções",

		table: "Valores",
		harm: "Ordem   ",
		value: "Valor",
	},
	es: {
		title: "Armónicos",
		show: "Mostrar",
		showOptions: ["Valores", "Procentajes"],
		mode: "Cantidad",
		all: "Todas",
		minMax: "Mostrar máximos",

		options: "Opciones",

		table: "Valores",
		harm: "Orden   ",
		value: "Valor",
	},
} as const);

export const PHarmonics : DvcPage = {
  page: Content,
  path: route,
  title: glob,
  icon: harmIcon,

  available(dvc) {
    return dvc._type !== TYPE_ICR
  }
}

